import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  LogOut,
  LogIn,
  Shield,
  User,
  Building2,
  HelpCircle,
  Lock,
  RotateCcw,
} from 'lucide-react';

import Joyride, { Step } from 'react-joyride';
import ProfilesTourSteps from '@/components/tour/ProfilesTourSteps';
import { getTourStyles } from '@/components/tour/tourStyles';

import { supabase } from '@/lib/supabaseClient';
import { logoutUser } from '@/components/utils/logoutUser';
import { useProfiles } from '@/components/hooks/useProfiles';
import { rutaInicioPanel } from '@/components/utils/rutasPanel';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

// Estilos por rol (badge + avatar)
const roleStyles: Record<string, { badge: string; avatar: string; dot: string }> = {
  empresa: {
    badge: 'bg-[#e7effa] text-[#1a4d7a] dark:bg-[#1a4d7a]/20 dark:text-[#7fb0e0]',
    avatar: 'bg-[#e7effa] text-[#1a4d7a] dark:bg-[#1a4d7a]/20 dark:text-[#7fb0e0]',
    dot: 'bg-[#1a4d7a] dark:bg-[#7fb0e0]',
  },
  trabajador: {
    badge: 'bg-[#e6f5ee] text-[#1c7a52] dark:bg-[#1c7a52]/20 dark:text-[#5cc999]',
    avatar: 'bg-[#e6f5ee] text-[#1c7a52] dark:bg-[#1c7a52]/20 dark:text-[#5cc999]',
    dot: 'bg-[#1c7a52] dark:bg-[#5cc999]',
  },
  cliente: {
    badge: 'bg-[#eef0f5] text-[#103254] dark:bg-white/10 dark:text-[#9db4cc]',
    avatar: 'bg-[#eef0f5] text-[#103254] dark:bg-white/10 dark:text-[#9db4cc]',
    dot: 'bg-[#103254] dark:bg-[#9db4cc]',
  },
  administrador: {
    badge: 'bg-[#f3ece2] text-[#8a5a1c] dark:bg-[#8a5a1c]/20 dark:text-[#d6a869]',
    avatar: 'bg-[#f3ece2] text-[#8a5a1c] dark:bg-[#8a5a1c]/20 dark:text-[#d6a869]',
    dot: 'bg-[#8a5a1c] dark:bg-[#d6a869]',
  },
};

const getInitials = (name?: string) =>
  (name ?? '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

const Profiles: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [runTour, setRunTour] = useState(false);
  const isMobile = window.innerWidth < 640;

  const { perfilesOrdenados, loading, error, updateInicioAutomatico } = useProfiles();

  // Perfil de cliente dado de baja por el propio usuario. Va en una consulta
  // aparte porque useProfiles tira de vista_usuarios_detalle, que filtra
  // activo = true y la consumen también el login y los dos registros.
  // Solo el perfil de cliente puede darse de baja y reactivarse a sí mismo.
  const [perfilDadoBaja, setPerfilDadoBaja] = useState<any | null>(null);

  useEffect(() => {
    let cancelado = false;

    const cargarBaja = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) return;

      const { data } = await supabase
        .from('vista_usuarios_perfiles_todos')
        .select('id_usuario, id_perfil, id_empresa, perfil, nombre_perfil, fecha_baja')
        .eq('id_usuario', session.user.id)
        .eq('perfil', 'cliente')
        .eq('activo', false)
        .not('fecha_baja', 'is', null)
        .limit(1)
        .maybeSingle();

      if (!cancelado && data) setPerfilDadoBaja(data);
    };

    cargarBaja();

    return () => {
      cancelado = true;
    };
  }, []);

  const steps: Step[] = ProfilesTourSteps;

  const labelPanel = (p: any) => {
    if (p.perfil === 'empresa') return t('profiles.panelEmpresa');
    if (p.perfil === 'trabajador') return t('profiles.panelTrabajador');
    if (p.perfil === 'cliente') return t('profiles.panelCliente');
    if (p.perfil === 'administrador') return t('profiles.panelAdmin');
    return t('profiles.panel');
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const handlePerfilClick = (perfil: any) => {
    localStorage.removeItem('id_perfil');
    localStorage.removeItem('id_empresa');
    localStorage.setItem('id_perfil', perfil.id_perfil || '');
    localStorage.setItem('id_empresa', perfil.id_empresa || '');
    navigate(rutaInicioPanel(perfil.ruta_panel));
  };

  const handleSwitchChange = async (perfil: any) => {
    if (!perfil.id_usuario || !perfil.id_perfil) return;
    await updateInicioAutomatico(perfil);
  };

  // El acceso del trabajador ya viene resuelto en la vista
  // (acceso_bloqueado). Solo aplica a perfiles de tipo trabajador.
  const trabajadorBloqueado = (perfil: any) =>
    perfil.perfil === 'trabajador' && perfil.acceso_bloqueado === true;

  const isLoading = loading;

  const hayContenido = perfilesOrdenados.length > 0 || perfilDadoBaja !== null;

  return (
    <div className="flex flex-col min-h-screen bg-[#f4f6f9] dark:bg-black font-sans">
      {/* HERO */}
      <div className="relative overflow-hidden pb-32">
        {/* Fondo modo claro: navy → teal, con resplandores radiales */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 20%, rgba(40,142,127,0.28) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 70%, rgba(26,77,122,0.35) 0%, transparent 55%),
              linear-gradient(160deg, #0c2942 0%, #103254 45%, #1a4d7a 100%)
            `,
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, funde con el resto de la app */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 20%, rgba(40,142,127,0.3) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 70%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(160deg, #050807 0%, #0a1a16 30%, #103254 70%, #000000 100%)
            `,
          }}
        />

        {/* Glows decorativos animados */}
        <motion.div
          className="pointer-events-none absolute -top-16 -right-10 h-80 w-80 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(40,142,127,0.22) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="pointer-events-none absolute -bottom-10 -left-16 h-64 w-64 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        />

        {/* Top bar */}
        <header className="relative z-10 mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-5">
          <img
            src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
            alt="Bookoll Logo"
            className="h-10 sm:h-11 object-contain"
          />
          <Button
            onClick={handleLogout}
            size="sm"
            className="rounded-xl border border-red-500/40 dark:border-red-500/60 bg-red-500 dark:bg-red-600 text-white backdrop-blur-sm hover:bg-red-600 dark:hover:bg-red-700 hover:border-red-600"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">{t('profiles.logout')}</span>
          </Button>
        </header>

        {/* Bienvenida */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative z-10 mx-auto mt-6 max-w-2xl px-6 text-center"
        >
          <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-[0.14em] text-white/60">
            {t('profiles.welcome')}
          </span>
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-white">
            {t('profiles.title')}
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-[15px] leading-relaxed text-white/70">
            {t('profiles.subtitle')}
          </p>
        </motion.div>

        {/* Wave inferior: funde el hero con el contenido */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-14 sm:h-20">
            <path
              d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z"
              className="fill-[#f4f6f9] dark:fill-black"
            />
          </svg>
        </div>
      </div>

      {/* CONTENIDO (solapa el hero) */}
      <main className="relative z-10 mx-auto -mt-24 w-full max-w-5xl flex-1 px-6">
        {/* Ayuda / tour — en su posición original, dentro del flujo normal
            (no fijo), solo icono, alineado a la derecha del contenedor. */}
        <div className="mb-4 hidden sm:flex justify-end">
          <button
            type="button"
            onClick={() => setRunTour(true)}
            className="flex items-center gap-2 px-2 py-2 rounded-full text-white/85 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Ayuda"
          >
            <HelpCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Ayuda</span>
          </button>
        </div>

        {/* TOUR */}
        <Joyride
          steps={steps}
          run={runTour}
          continuous
          showSkipButton
          spotlightClicks
          styles={getTourStyles(isMobile)}
          locale={{
            back: t('profiles.tour.back'),
            close: t('profiles.tour.close'),
            last: t('profiles.tour.last'),
            next: t('profiles.tour.next'),
            skip: t('profiles.tour.skip'),
          }}
          callback={({ status }) => {
            if (status === 'finished' || status === 'skipped') setRunTour(false);
          }}
        />

        {/* LISTA */}
        {isLoading && !hayContenido ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#103254]/15 border-t-[#103254] dark:border-blue-400/20 dark:border-t-blue-400" />
          </div>
        ) : !hayContenido ? (
          <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            {error || t('profiles.noProfiles')}
          </p>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {perfilesOrdenados.map((perfil) => {
              const titulo = perfil.empresa ?? perfil.nombre_perfil;
              const subtitulo = labelPanel(perfil);
              const rs = roleStyles[perfil.perfil] ?? roleStyles.cliente;
              const bloqueado = trabajadorBloqueado(perfil);

              return (
                <motion.div
                  key={`${perfil.id_usuario}-${perfil.id_perfil}-${perfil.id_empresa || 'none'}`}
                  variants={itemVariants}
                  whileHover={bloqueado ? undefined : { y: -3 }}
                  className="tour-profile-card flex flex-col gap-4 rounded-2xl border border-[#e6eaf0] bg-white p-5 shadow-[0_10px_30px_-18px_rgba(16,50,84,0.35)] transition-shadow hover:shadow-[0_22px_44px_-22px_rgba(16,50,84,0.42)] dark:border-white/10 dark:bg-[#1c1c1e]"
                >
                  {/* Fila superior: switch */}
                  <div className="flex items-center justify-end">
                    <div className="tour-switch flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                        {t('profiles.autoStart')}
                      </span>
                      <Switch
                        checked={perfil.inicio_automatico}
                        disabled={isLoading || bloqueado}
                        onCheckedChange={() => handleSwitchChange(perfil)}
                      />
                    </div>
                  </div>

                  {/* Identidad */}
                  <div className="flex items-center gap-3.5">
                    {perfil.perfil === 'administrador' ? (
                      <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${rs.avatar}`}>
                        <Shield className="h-6 w-6" />
                      </div>
                    ) : perfil.perfil === 'cliente' ? (
                      <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${rs.avatar}`}>
                        <User className="h-6 w-6" />
                      </div>
                    ) : perfil.logo ? (
                      <img
                        src={`${perfil.logo}?t=${Date.now()}`}
                        alt="Logo empresa"
                        className="h-14 w-14 rounded-2xl border border-gray-200 object-contain dark:border-white/10"
                      />
                    ) : (
                      <div className={`flex h-14 w-14 items-center justify-center rounded-2xl text-lg font-extrabold ${rs.avatar}`}>
                        {perfil.perfil === 'empresa' ? <Building2 className="h-6 w-6" /> : getInitials(titulo)}
                      </div>
                    )}

                    <div className="min-w-0">
                      <h3 className="truncate text-base font-bold text-[#0f1e2e] dark:text-white">
                        {titulo}
                      </h3>
                      <p className="text-[13px] text-gray-500 dark:text-gray-400">{subtitulo}</p>
                    </div>
                  </div>

                  {/* Botón acceso — bloqueado si el trabajador no tiene acceso */}
                  {bloqueado ? (
                    <div className="mt-1">
                      <Button
                        disabled
                        variant="outline"
                        className="w-full rounded-xl py-3 font-bold cursor-not-allowed !bg-red-100 !text-red-600 !border-red-200 hover:!bg-red-100 hover:!text-red-600 disabled:!opacity-100 dark:!bg-red-900/30 dark:!text-red-400 dark:!border-red-900/40"
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        Sin acceso
                      </Button>
                      <p className="mt-2 text-[12px] leading-relaxed text-amber-600 dark:text-amber-500 text-center">
                        No tienes acceso a este panel. Ponte en contacto con tu empresa.
                      </p>
                    </div>
                  ) : (
                    <Button
                      onClick={() => handlePerfilClick(perfil)}
                      className="tour-login-btn mt-1 w-full rounded-xl py-3 font-bold"
                    >
                      <LogIn className="mr-2 h-4 w-4" />
                      {t('profiles.signIn')}
                    </Button>
                  )}
                </motion.div>
              );
            })}

            {/* Perfil de cliente dado de baja — siempre al final, en gris y sin
                efecto hover, para que se distinga de los activos. */}
            {perfilDadoBaja && (
              <motion.div
                key={`baja-${perfilDadoBaja.id_perfil}`}
                variants={itemVariants}
                className="flex flex-col gap-4 rounded-2xl border border-dashed border-[#d6dbe3] bg-[#fafbfc] p-5 dark:border-white/10 dark:bg-white/[0.03]"
              >
                {/* Fila superior: switch deshabilitado, para conservar la
                    misma altura que el resto de tarjetas. */}
                <div className="flex items-center justify-end">
                  <div className="flex items-center gap-2 opacity-40">
                    <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                      {t('profiles.autoStart')}
                    </span>
                    <Switch checked={false} disabled />
                  </div>
                </div>

                <div className="flex items-center gap-3.5 opacity-60">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef0f5] text-[#103254] dark:bg-white/10 dark:text-[#9db4cc]">
                    <User className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-bold text-[#0f1e2e] dark:text-white">
                      {perfilDadoBaja.nombre_perfil ?? 'Cliente'}
                    </h3>
                    <p className="text-[13px] text-gray-500 dark:text-gray-400">
                      Perfil desactivado
                    </p>
                  </div>
                </div>

                <div className="mt-1">
                  <Button
                    onClick={() => navigate('/reactivar-perfil')}
                    variant="outline"
                    className="w-full rounded-xl py-3 font-bold border-[#288E7F]/40 text-[#1e7a6e] hover:bg-[#288E7F]/10 dark:border-[#288E7F]/50 dark:text-[#5cc999] dark:hover:bg-[#288E7F]/15"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reactivar
                  </Button>
                  <p className="mt-2 text-[12px] leading-relaxed text-gray-500 dark:text-gray-400 text-center">
                    Diste de baja este perfil. Puedes recuperarlo cuando quieras.
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </main>

      {/* FOOTER */}
      <footer className="mt-14 border-t border-[#e6eaf0] bg-white py-6 text-center text-[13px] text-gray-400 dark:border-white/10 dark:bg-black dark:text-gray-500">
        © {new Date().getFullYear()} Bookoll · {t('profiles.footerTag')}
      </footer>
    </div>
  );
};

export default Profiles;